import { Component, OnInit, OnDestroy, Output, EventEmitter, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { ReplayService, ReplayState } from '../../../services/replay.service';
import { ReplaySettingsSheetComponent } from '../replay-settings-sheet/replay-settings-sheet.component';
import { TourAnchorDirective } from '../../../features/learning-center/engine/tour-anchor.directive';

@Component({
  selector: 'app-replay-overlay',
  standalone: true,
  imports: [CommonModule, FormsModule, TourAnchorDirective],
  templateUrl: './replay-overlay.component.html',
  styleUrl: './replay-overlay.component.scss',
})
export class ReplayOverlayComponent implements OnInit, OnDestroy {
  /** Initial filter state passed in from the parent after filter sheet closes. */
  @Input() initialPresetIndex = 2;
  @Input() initialSourceIndex = 0;
  @Input() initialCustomRange: { start: Date; end: Date } | null = null;

  /** Emits when the user taps × to fully exit replay mode. */
  @Output() closed = new EventEmitter<void>();

  /** Emits whenever the user saves new filters inside the settings sheet. */
  @Output() filterChanged = new EventEmitter<{
    presetIndex: number;
    sourceIndex: number;
    customRange: { start: Date; end: Date } | null;
  }>();

  state: ReplayState | null = null;

  private _sub?: Subscription;
  private _selectedPresetIndex = 2;
  private _selectedSourceIndex = 0;
  private _activeCustomRange: { start: Date; end: Date } | null = null;

  constructor(
    public replayService: ReplayService,
    private dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    this._selectedPresetIndex = this.initialPresetIndex;
    this._selectedSourceIndex = this.initialSourceIndex;
    this._activeCustomRange   = this.initialCustomRange;

    this._sub = this.replayService.state$.subscribe(s => (this.state = s));

    // Auto-start playback when overlay first mounts
    if (!this.replayService.isLoading && this.replayService.hasData) {
      const s = this.state;
      if (s && !s.isPlaying) {
        this.replayService.togglePlay();
      }
    }
  }

  ngOnDestroy(): void {
    this._sub?.unsubscribe();
  }

  get progress(): number {
    return this.state?.tripProgress ?? 0;
  }

  get isPlaying(): boolean {
    return this.state?.isPlaying ?? false;
  }

  get speed(): number {
    return this.state?.playbackSpeed ?? 1;
  }

  get tripCount(): number {
    return this.state?.tripCount ?? 0;
  }

  get activeCount(): number {
    return this.state?.activeBoats?.length ?? 0;
  }

  get isLoading(): boolean {
    return this.state?.isLoading ?? false;
  }

  get currentTimeLabel(): string {
    const t = this.state?.currentTime;
    if (!t) return '—';
    // Mirror Flutter: EEE MMM d yyyy  HH:mm:ss TZ  (24-hour + timezone abbreviation)
    return t.toLocaleString('en-US', {
      weekday: 'short', month: 'short', day: 'numeric',
      year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit',
      hour12: false, timeZoneName: 'short',
    });
  }

  onSliderChange(val: number): void {
    if (this.isPlaying) this.replayService.pause();
    this.replayService.seekToProgress(val);
  }

  togglePlay(): void {
    this.replayService.togglePlay();
  }

  cycleSpeed(): void {
    this.replayService.cycleSpeed();
  }

  openSettings(): void {
    const ref = this.dialog.open(ReplaySettingsSheetComponent, {
      panelClass: 'replay-settings-dialog',
      data: {
        presetIndex:  this._selectedPresetIndex,
        sourceIndex:  this._selectedSourceIndex,
        customRange:  this._activeCustomRange,
      },
    });

    ref.afterClosed().subscribe((result: any) => {
      if (!result) return;
      const presetChanged = result.presetIndex !== this._selectedPresetIndex;
      const sourceChanged = result.sourceIndex !== this._selectedSourceIndex;
      const rangeChanged  = result.customRange !== this._activeCustomRange;

      this._selectedPresetIndex = result.presetIndex;
      this._selectedSourceIndex = result.sourceIndex;
      this._activeCustomRange   = result.customRange;

      this.filterChanged.emit({
        presetIndex: result.presetIndex,
        sourceIndex: result.sourceIndex,
        customRange: result.customRange,
      });

      if (presetChanged || sourceChanged || rangeChanged) {
        if (presetChanged && result.presetIndex !== -1) {
          const days = [1, 7, 30][result.presetIndex] ?? 30;
          this.replayService.setHistoryDays(days);
        }
        this.replayService.fetchAndLoadTrips({
          showMine:   result.sourceIndex === 0 || result.sourceIndex === 1,
          showShared: result.sourceIndex === 0 || result.sourceIndex === 2,
          dateRange:  result.customRange,
        });
      }
    });
  }

  close(): void {
    this.closed.emit();
  }
}
